"use client";

import { useEffect, useRef, useState } from "react";
import QosmCode from "./QosmCode";
import { accent } from "@/lib/palette";

const mcpServers = [
  {
    name: "GitHub",
    capabilities: ["create_issue", "search_repos"],
    color: "#c4b5fd",
  },
  {
    name: "Slack",
    capabilities: ["send_message", "list_channels"],
    color: "#c4b5fd",
  },
  {
    name: "PostgreSQL",
    capabilities: ["query", "insert"],
    color: "#c4b5fd",
  },
  {
    name: "Stripe",
    capabilities: ["create_charge", "list_customers"],
    color: "#c4b5fd",
  },
  {
    name: "Filesystem",
    capabilities: ["read_file", "list_dir"],
    color: "#c4b5fd",
  },
  {
    name: "Linkup",
    capabilities: ["web_search"],
    color: "#c4b5fd",
  },
];

const generatedPrelude = `// Auto-generated from connected MCP servers

extern create_issue  : {repo: String, title: String, body: String}
                    -> {number: Int, url: String} ! {GitHub};

extern search_repos  : {query: String, limit: Int}
                    -> Array<{name: String, stars: Int, url: String}> ! {GitHub};

extern send_message  : {channel: String, text: String}
                    -> {ts: String} ! {Slack};

extern query         : {sql: String, params: Array<String>}
                    -> Array<{row: {col: String, val: String}}> ! {PostgreSQL};

extern create_charge : {amount: Int, currency: String, customer: String}
                    -> {id: String, status: String} ! {Stripe};

extern web_search    : {query: String}
                    -> Array<{title: String, url: String, snippet: String}> ! {Linkup};`;

function ScrollReveal({
  children,
  delay = 0,
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-500 ease-out ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export default function MCPSection() {
  return (
    <section className="py-24 px-4 max-md:py-16">
      <div className="max-w-[900px] mx-auto">
        <div className="text-center mb-12 max-md:mb-8">
          <h2 className="text-[clamp(1.75rem,4vw,2.5rem)] font-bold text-text-primary tracking-tight mb-4">
            <span style={{ color: "#93c5fd" }}>MCP tools</span> are capabilities.
          </h2>
          <p className="text-lg text-text-secondary max-w-[600px] mx-auto leading-relaxed max-md:text-base">
            Connect to any MCP server. Every tool becomes a typed function the agent can call.
          </p>
        </div>

        {/* MCP server grid */}
        <ScrollReveal>
          <div className="grid grid-cols-3 gap-3 mb-8 max-md:grid-cols-2">
            {mcpServers.map((server, i) => (
              <div
                key={server.name}
                className="rounded-lg border border-border bg-bg-elevated px-4 py-3.5 max-md:px-3 max-md:py-3"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <span
                    className="w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ backgroundColor: accent.DEFAULT }}
                  />
                  <span className="text-sm font-semibold text-text-primary">
                    {server.name}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  {server.capabilities.map((cap) => (
                    <span
                      key={cap}
                      className="font-mono text-[11px] text-text-muted"
                    >
                      {cap}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Arrow */}
        <ScrollReveal delay={100}>
          <div className="flex flex-col items-center py-2 mb-6">
            <div className="w-px h-6 bg-border" />
            <svg
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
              className="text-border"
            >
              <path
                d="M1 1L6 6L11 1"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </ScrollReveal>

        {/* Generated prelude */}
        <ScrollReveal delay={200}>
          <div className="rounded-xl overflow-hidden border border-border">
            <div className="bg-bg-surface px-4 py-2.5 flex justify-between items-center border-b border-border">
              <span className="font-mono text-xs text-text-muted">
                generated prelude
              </span>
              <span className="text-[0.6rem] font-semibold uppercase tracking-widest text-text-muted">
                Auto-generated
              </span>
            </div>
            <div className="bg-bg-elevated">
              <QosmCode
                code={generatedPrelude}
                className="m-0 px-5 pt-4 pb-4 text-[13px] leading-relaxed overflow-x-auto max-md:text-sm max-md:px-4"
              />
            </div>
            <div className="px-5 py-3 bg-bg-primary border-t border-border">
              <p className="text-xs text-text-muted leading-relaxed">
                The host connects to MCP servers and generates typed capability
                declarations. The agent code stays the same.
              </p>
            </div>
          </div>
        </ScrollReveal>

      </div>
    </section>
  );
}
